import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Truck, Package, RotateCcw, Check, X, Camera, Phone, MapPin,
  Navigation, ChevronDown, ChevronUp, AlertTriangle, CheckSquare,
  Square, Pen, Clock, User, FileText, ArrowRight, Zap
} from 'lucide-react';
import SignatureCanvasComponent from '../components/common/SignatureCanvasComponent';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Couleurs statuts ──────────────────────────────────────────────────────
const MISSION_COLORS = {
  chargement: { bg: '#6366F1', label: 'Chargement', icon: <Package size={16}/> },
  livraison:  { bg: '#3B82F6', label: 'Livraison',  icon: <Truck size={16}/> },
  retour:     { bg: '#F97316', label: 'Retour',      icon: <RotateCcw size={16}/> },
};

const ETAT_OPTIONS = [
  { key: 'conforme',      label: '✅ Conforme',           color: '#22C55E' },
  { key: 'anomalie',      label: '⚠️ Anomalie',           color: '#F59E0B' },
  { key: 'manquant',      label: '❌ Manquant',           color: '#EF4444' },
  { key: 'non_controle',  label: '🔵 Non contrôlé',       color: '#9CA3AF' },
];

// ─── Composant signature — thème sombre admin ────────────────────────────
// Canvas intentionnellement blanc pour un rendu PDF correct (trait sombre sur fond blanc)
function SignaturePad({ onSave, clientNom, mention }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [signed, setSigned] = useState(false);

  // Initialiser le fond blanc + résolution Retina
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = (rect.width  || 340) * dpr;
    canvas.height = 120 * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }
  function startDraw(e) {
    e.preventDefault();
    setDrawing(true);
    setLastPos(getPos(e, canvasRef.current));
  }
  function draw(e) {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1A1A2E';   // trait foncé → lisible sur fond blanc + PDF
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setLastPos(pos);
    setSigned(true);
  }
  function stopDraw() { setDrawing(false); }
  function clear() {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
  }
  function save() {
    if (!signed) { toast.error('Veuillez signer avant de valider'); return; }
    onSave(canvasRef.current.toDataURL('image/png'), clientNom);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Mention légale — fond sombre */}
      <div style={{
        padding: '10px 14px', borderRadius: 10,
        background: 'var(--admin-teal-dim)', border: '1px solid var(--admin-teal-line)',
        fontSize: 12, color: 'var(--admin-text)', lineHeight: 1.6,
      }}>
        <strong style={{ color: 'var(--admin-teal)' }}>Mention légale :</strong> {mention}
      </div>
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--admin-muted)' }}>
        Signature de <strong style={{ color: 'var(--admin-text)' }}>{clientNom}</strong>
      </p>
      {/* Canvas — fond blanc intentionnel pour lisibilité PDF */}
      <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid var(--admin-teal-line)' }}>
        <canvas
          ref={canvasRef}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
          style={{ display: 'block', width: '100%', height: 120, background: '#FFFFFF', touchAction: 'none', cursor: 'crosshair' }}
        />
      </div>
      <p style={{ fontSize: 10, color: 'var(--admin-faint)', textAlign: 'center' }}>
        Fond blanc maintenu pour export PDF
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={clear} style={{
          flex: 1, padding: '10px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)',
        }}>Effacer</button>
        <button onClick={save} style={{
          flex: 1, padding: '10px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          background: 'var(--admin-green)', border: 'none', color: '#052b25',
        }}>✅ Valider la signature</button>
      </div>
    </div>
  );
}

// ─── Composant photo terrain ───────────────────────────────────────────────
function PhotoCapture({ onPhoto, label = "Ajouter une photo" }) {
  const fileRef = useRef(null);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onPhoto(ev.target.result, file.name);
    reader.readAsDataURL(file);
  }

  return (
    <button
      onClick={() => fileRef.current?.click()}
      className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
      style={{ background: 'rgba(93,188,191,0.1)', border: '1px solid rgba(93,188,191,0.3)', color: '#0B4B4E', cursor: 'pointer' }}>
      <Camera size={14} /> {label}
      <input ref={fileRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={handleFile} />
    </button>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────
export default function FicheMissionPage() {
  const [searchParams] = useSearchParams();
  const quoteIdFromURL = searchParams.get('quote'); // ?quote=UUID depuis AdminLivraisons

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [missions, setMissions] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [missionType, setMissionType] = useState('livraison');
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('list'); // list | checklist | photos | signature | done
  const [photos, setPhotos] = useState([]);
  const [showSignature, setShowSignature] = useState(false);
  const [signed, setSigned] = useState(false);
  const [forceModal, setForceModal] = useState(false);
  const [forceRaison, setForceRaison] = useState('');

  // ── Chargement direct par ID (sans attendre la liste) ────────────────────
  // Quand ?quote=UUID est dans l'URL, on charge directement le devis par son ID.
  // Cela évite la race condition où openMission dépendait du chargement préalable
  // de la liste complète.
  const directLoadDone = React.useRef(false);

  useEffect(() => {
    if (!quoteIdFromURL || directLoadDone.current) return;
    directLoadDone.current = true;

    async function loadDirectMission() {
      setLoading(true);
      try {
        // Charger le devis directement par son ID
        const res = await fetch(`${BACKEND}/api/devis/${quoteIdFromURL}`, { headers: H });
        if (!res.ok) throw new Error('Devis non trouvé');
        const quote = await res.json();
        // Ouvrir la mission sans passer par la liste
        await openMission(quote, 'livraison');
      } catch {
        // Fallback : attendre le chargement de la liste
      } finally {
        setLoading(false);
      }
    }
    loadDirectMission();
  // eslint-disable-next-line
  }, [quoteIdFromURL]);

  // Charger les commandes actives
  const loadMissions = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/devis?limit=50`, { headers: H });
      const data = await res.json();
      const actives = (data.quotes || []).filter(q =>
        ['produits_charges', 'en_livraison', 'livree', 'en_location',
         'retour_a_effectuer', 'a_preparer', 'payee', 'contrat_signe',
         // anciens statuts
         'solde_paye', 'valide_admin', 'confirme', 'acompte_paye', 'signe'
        ].includes(q.statut)
      );
      setMissions(actives);
    } catch { }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { loadMissions(); }, [loadMissions]);

  async function openMission(quote, type) {
    setSelectedQuote(quote);
    setMissionType(type);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/missions/${quote.id}/${type}`, { headers: H });
      const data = await res.json();
      setMission(data);
      setPhotos(data.photos || []);
      setSigned(!!data.signature);
      setStep('checklist');
    } catch { toast.error('Erreur chargement mission'); }
    setLoading(false);
  }

  async function toggleCheck(productId, checked) {
    await fetch(`${BACKEND}/api/missions/${selectedQuote.id}/${missionType}/checklist/${productId}`, {
      method: 'PUT', headers: H, body: JSON.stringify({ checked })
    });
    setMission(m => ({
      ...m,
      checklist: (m.checklist || []).map(i =>
        i.product_id === productId ? { ...i, checked } : i
      )
    }));
  }

  async function setEtat(productId, etat) {
    await fetch(`${BACKEND}/api/missions/${selectedQuote.id}/${missionType}/checklist/${productId}`, {
      method: 'PUT', headers: H, body: JSON.stringify({ etat, checked: etat !== 'non_controle' })
    });
    setMission(m => ({
      ...m,
      checklist: (m.checklist || []).map(i =>
        i.product_id === productId ? { ...i, etat, checked: etat !== 'non_controle' } : i
      )
    }));
  }

  async function addPhoto(base64, caption) {
    const res = await fetch(`${BACKEND}/api/missions/${selectedQuote.id}/${missionType}/photos`, {
      method: 'POST', headers: H,
      body: JSON.stringify({ base64, url: base64, caption, taken_by: 'admin' })
    });
    const data = await res.json();
    setPhotos(prev => [...prev, data]);
    toast.success('Photo ajoutée');
  }

  async function saveSignature(signatureDataUrl) {
    await fetch(`${BACKEND}/api/missions/${selectedQuote.id}/${missionType}/signature`, {
      method: 'POST', headers: H,
      body: JSON.stringify({
        signature_data_url: signatureDataUrl,
        client_nom: selectedQuote.client_nom,
      })
    });
    setSigned(true);
    setShowSignature(false);
    toast.success('Signature enregistrée !');
  }

  async function validerMission(force = false) {
    const checklist = mission?.checklist || [];
    const allChecked = checklist.every(i => i.checked);

    if (!allChecked && !force) {
      setForceModal(true);
      return;
    }

    const res = await fetch(`${BACKEND}/api/missions/${selectedQuote.id}/${missionType}/valider`, {
      method: 'POST', headers: H,
      body: JSON.stringify({
        force_validation: force,
        force_raison: forceRaison,
        validated_by: 'admin',
      })
    });
    const data = await res.json();
    if (data.success) {
      toast.success(`Mission ${missionType} validée ! ✅`);
      setStep('done');
      loadMissions();
    }
  }

  const checklist = mission?.checklist || [];
  const checked = checklist.filter(i => i.checked).length;
  const total = checklist.length;
  const allChecked = checked === total;
  const missionConf = MISSION_COLORS[missionType] || MISSION_COLORS.livraison;

  // ── Vue liste des commandes ──
  if (step === 'list') {
    return (
      <div style={{ background: 'var(--admin-bg)', minHeight: '100vh', padding: '1rem', fontFamily: 'var(--admin-font-sans)', color: 'var(--admin-text)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--admin-teal-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-teal)' }}>
            <Truck size={20} />
          </div>
          <div>
            <h1 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 22, fontWeight: 700,
              color: 'var(--admin-text)', margin: 0 }}>Missions terrain</h1>
            <p style={{ fontSize: 12, color: 'var(--admin-faint)', margin: '2px 0 0' }}>{missions.length} mission(s) active(s)</p>
          </div>
        </div>

        {missions.length === 0 ? (
          <div className="text-center py-16">
            <CheckSquare size={48} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
            <p style={{ color: 'var(--admin-muted)' }}>Aucune mission en cours</p>
          </div>
        ) : (
          <div className="space-y-3">
            {missions.map(q => (
              <div key={q.id} className="rounded-2xl p-4"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{q.client_nom}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{q.numero}</p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full font-semibold"
                    style={{ background: '#5DBCBF20', color: '#0B4B4E' }}>
                    {q.statut}
                  </span>
                </div>

                {q.client_telephone && (
                  <a href={`tel:${q.client_telephone}`}
                    className="flex items-center gap-2 text-sm mb-2 font-semibold"
                    style={{ color: '#5DBCBF' }}>
                    <Phone size={14} /> {q.client_telephone}
                  </a>
                )}

                <div className="grid grid-cols-3 gap-2 mt-3">
                  {[
                    { type: 'chargement', conf: MISSION_COLORS.chargement },
                    { type: 'livraison',  conf: MISSION_COLORS.livraison },
                    { type: 'retour',     conf: MISSION_COLORS.retour },
                  ].map(({ type, conf }) => (
                    <button key={type} onClick={() => openMission(q, type)}
                      disabled={loading}
                      className="py-2 rounded-xl text-xs font-bold flex flex-col items-center gap-1"
                      style={{ background: `${conf.bg}15`, color: conf.bg, border: `1px solid ${conf.bg}30` }}>
                      {conf.icon}
                      {conf.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Vue done ──
  if (step === 'done') {
    return (
      <div style={{ background: 'var(--admin-bg)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div className="text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="font-bold text-xl mb-2" style={{ color: 'var(--admin-text)' }}>Mission validée !</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--admin-muted)' }}>
            La mission <strong>{missionType}</strong> pour {selectedQuote?.client_nom} est terminée.
          </p>
          <button onClick={() => { setStep('list'); setMission(null); setSelectedQuote(null); }}
            className="btn-teal px-6 py-3 rounded-xl font-bold text-sm">
            Retour aux missions
          </button>
        </div>
      </div>
    );
  }

  // ── Vue mission détail ──
  return (
    <div style={{ background: 'var(--admin-bg)', minHeight: '100vh' }}>
      {/* Header mission */}
      <div className="p-4 sticky top-0 z-10"
        style={{ background: missionConf.bg, color: '#fff' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => setStep('list')}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '6px 10px', color: '#fff', cursor: 'pointer' }}>
            ← Retour
          </button>
          <div className="flex-1">
            <p className="font-bold text-sm">{selectedQuote?.client_nom}</p>
            <p className="text-xs opacity-80">{selectedQuote?.numero} · {missionConf.label}</p>
          </div>
          <div className="text-right">
            <p className="text-xs opacity-80">Progression</p>
            <p className="font-bold text-sm">{checked}/{total}</p>
          </div>
        </div>
        {/* Barre de progression */}
        <div className="mt-2 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.3)' }}>
          <div className="h-full rounded-full transition-all"
            style={{ background: '#fff', width: total > 0 ? `${(checked / total) * 100}%` : '0%' }} />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Infos client + boutons action */}
        <div className="rounded-2xl p-4" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
          <div className="flex items-start gap-3 mb-3">
            <User size={16} style={{ color: 'var(--admin-teal)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{selectedQuote?.client_nom}</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{selectedQuote?.client_email}</p>
            </div>
          </div>
          {mission?.adresse_livraison && (
            <div className="flex items-start gap-3 mb-3">
              <MapPin size={16} style={{ color: 'var(--admin-teal)', flexShrink: 0, marginTop: 2 }} />
              <p className="text-xs" style={{ color: 'var(--admin-text)' }}>{mission.adresse_livraison}</p>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            {selectedQuote?.client_telephone && (
              <a href={`tel:${selectedQuote.client_telephone}`}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: '#5DBCBF15', color: '#5DBCBF', border: '1px solid rgba(93,188,191,0.3)', textDecoration: 'none' }}>
                <Phone size={14} /> Appeler
              </a>
            )}
            {mission?.adresse_livraison && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(mission.adresse_livraison)}`}
                target="_blank" rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: '#3B82F615', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.3)', textDecoration: 'none' }}>
                <Navigation size={14} /> GPS
              </a>
            )}
          </div>
        </div>

        {/* Checklist produits */}
        <div>
          <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--admin-text)' }}>
            {missionType === 'chargement' ? '📦 Produits à charger' :
             missionType === 'retour' ? '🔍 Contrôle des produits' : '✅ Produits à livrer'}
          </h3>
          <div className="space-y-3">
            {checklist.map(item => (
              <div key={item.product_id} className="rounded-2xl p-3"
                style={{ background: '#FFFFFF', border: `1.5px solid ${item.checked ? '#22C55E' : 'var(--border)'}` }}>
                <div className="flex items-start gap-3">
                  {item.product_image && (
                    <img src={item.product_image} alt={item.product_nom}
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm mb-1" style={{ color: 'var(--admin-text)' }}>{item.product_nom}</p>
                    <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>Quantité : {item.quantite}</p>

                    {/* Retour : sélecteur d'état */}
                    {missionType === 'retour' ? (
                      <div className="flex flex-wrap gap-1">
                        {ETAT_OPTIONS.map(opt => (
                          <button key={opt.key}
                            onClick={() => setEtat(item.product_id, opt.key)}
                            className="text-xs px-2 py-1 rounded-lg font-semibold"
                            style={{
                              background: item.etat === opt.key ? opt.color : `${opt.color}20`,
                              color: item.etat === opt.key ? '#fff' : opt.color,
                              border: 'none', cursor: 'pointer',
                            }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      /* Livraison/chargement : simple checkbox */
                      <button
                        onClick={() => toggleCheck(item.product_id, !item.checked)}
                        className="flex items-center gap-2 text-sm font-semibold"
                        style={{ border: 'none', background: 'none', cursor: 'pointer',
                                 color: item.checked ? '#22C55E' : 'var(--text-muted)' }}>
                        {item.checked ? <CheckSquare size={18} style={{ color: '#22C55E' }} /> : <Square size={18} />}
                        {item.checked ? 'Coché' : 'À cocher'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Photos terrain */}
        <div className="rounded-2xl p-4" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>📸 Photos ({photos.length})</h3>
            <PhotoCapture onPhoto={addPhoto} />
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {photos.slice(-6).map((p, i) => (
                <img key={i} src={p.url || p.base64} alt="photo"
                  className="w-full h-24 object-cover rounded-xl" loading="lazy" />
              ))}
            </div>
          )}
        </div>

        {/* Signature client */}
        {missionType !== 'chargement' && (
          <div className="rounded-2xl p-4" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>
                <Pen size={14} className="inline mr-1" />Signature client
              </h3>
              {signed && <span className="text-xs font-bold" style={{ color: '#22C55E' }}>✅ Signée</span>}
            </div>
            {!signed ? (
              showSignature ? (
                <SignaturePad
                  clientNom={selectedQuote?.client_nom || ''}
                  mention={
                    missionType === 'livraison'
                      ? "Je reconnais avoir reçu les produits loués, en bon état apparent, conformes à la commande, avec les accessoires prévus. Je confirme que la livraison a bien été effectuée."
                      : "Je reconnais que les produits loués ont été récupérés et contrôlés avec Show Événement. Les éventuelles remarques ou anomalies constatées sont indiquées dans la fiche de retour."
                  }
                  onSave={saveSignature}
                />
              ) : (
                <button onClick={() => setShowSignature(true)}
                  className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                  style={{ background: '#0B1E20', color: '#D4AF37', border: 'none', cursor: 'pointer' }}>
                  <Pen size={14} /> Faire signer le client
                </button>
              )
            ) : (
              <div className="p-3 rounded-xl text-center"
                style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <p className="text-sm font-semibold" style={{ color: '#16A34A' }}>✅ Client signé</p>
              </div>
            )}
          </div>
        )}

        {/* Bouton valider */}
        <button
          onClick={() => validerMission(false)}
          className="w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2"
          style={{
            background: allChecked ? missionConf.bg : '#9CA3AF',
            color: '#fff', border: 'none', cursor: 'pointer',
            boxShadow: allChecked ? `0 8px 24px ${missionConf.bg}50` : 'none',
          }}>
          {missionConf.icon}
          Valider la mission {missionConf.label}
          {!allChecked && ` (${total - checked} restant${total - checked > 1 ? 's' : ''})`}
        </button>

        {/* Modal force validation */}
        {forceModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.6)' }}>
            <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: '#fff' }}>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={20} style={{ color: '#F59E0B' }} />
                <h3 className="font-bold" style={{ color: 'var(--admin-text)' }}>Produits non cochés</h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--admin-muted)' }}>
                {total - checked} produit(s) non cochés. Souhaitez-vous forcer la validation avec une justification ?
              </p>
              <textarea
                value={forceRaison}
                onChange={e => setForceRaison(e.target.value)}
                placeholder="Justification obligatoire..."
                className="w-full p-3 rounded-xl text-sm mb-3 resize-none"
                style={{ border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}
                rows={3}
              />
              <div className="flex gap-2">
                <button onClick={() => setForceModal(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium"
                  style={{ background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)' }}>
                  Annuler
                </button>
                <button
                  onClick={() => { setForceModal(false); validerMission(true); }}
                  disabled={!forceRaison.trim()}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: forceRaison.trim() ? '#F59E0B' : '#9CA3AF', color: '#fff', border: 'none' }}>
                  Forcer la validation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
